/**
 * useCompetencyGrowth — calm hook for the Competency Growth sidebar.
 *
 * Stage-2 P3 (Feb 18, 2026). Doctrine:
 *   • Read-only by default. No automatic mutations.
 *   • Drive Mode aware — when moving / unknown the consumer surfaces
 *     a glance-only header; the hook still fetches prompts so the
 *     header can show a quiet competency hint.
 *   • Calm refresh — silent failures, never throws, never spams.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PROMPTS = Object.freeze({
  context: { focus: null, environment: null, complexity: null, stage: null },
  prompts: [],
  matched: 0,
  prompt: null,
  drive_mode: null,
});

const EMPTY_SUMMARY = Object.freeze({
  yesterday_count: 0,
  today_count: 0,
  today_aim: 1,
  summary: "Anything you spot today is a calm start.",
});

export default function useCompetencyGrowth({
  focus,
  environment,
  complexity,
  stage,
  movingState,
  enabled = true,
} = {}) {
  const [prompts, setPrompts] = useState(EMPTY_PROMPTS);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  const fetchPrompts = useCallback(async () => {
    if (!enabled) return;
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (focus) params.focus = focus;
      if (environment) params.environment = environment;
      if (complexity) params.complexity = complexity;
      if (stage) params.stage = stage;
      if (movingState) params.moving_state = movingState;
      const res = await axios.get(
        `${API_URL}/api/competency-growth/prompts`,
        { params, withCredentials: true },
      );
      if (id !== seq.current) return;
      setPrompts(res.data || EMPTY_PROMPTS);
    } catch (err) {
      if (id !== seq.current) return;
      setError(err?.response?.data?.detail || "unavailable");
      setPrompts(EMPTY_PROMPTS);
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [enabled, focus, environment, complexity, stage, movingState]);

  const fetchSummary = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/competency-growth/daily-summary`,
        { withCredentials: true },
      );
      setSummary(res.data || EMPTY_SUMMARY);
    } catch {
      setSummary(EMPTY_SUMMARY);
    }
  }, [enabled]);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const logEvidence = useCallback(
    async ({ lessonId, learnerId, competency, note }) => {
      if (!enabled) return { ok: false };
      try {
        const res = await axios.post(
          `${API_URL}/api/competency-growth/evidence`,
          {
            lesson_id:    lessonId,
            learner_id:   learnerId || null,
            competency,
            environment:  environment || null,
            focus:        focus || null,
            note:         note || null,
            moving_state: movingState || "unknown",
          },
          { withCredentials: true },
        );
        await fetchSummary();
        return { ok: true, ...res.data };
      } catch (err) {
        const detail = err?.response?.data?.detail;
        return {
          ok: false,
          code: detail?.code || "error",
          message: detail?.message || "Could not log evidence.",
        };
      }
    },
    [enabled, environment, focus, fetchSummary, movingState],
  );

  return {
    prompts,
    summary,
    loading,
    error,
    logEvidence,
    refresh: fetchPrompts,
  };
}
